import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Link,
  Typography,
  TextField,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import { useCallback, useState } from "react";
import { Button, SimpleForm, useDataProvider } from "react-admin";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useEffect } from "react";
import _, { debounce } from "lodash";

interface IAddStudentButton {
  handleAddStudent: (x: IStudent, y: IStudentAddress) => void;
  school: ISchool;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  addStudentButton: {
    width: "100%",
    marginLeft: 0,
    borderRadius: 0,
    padding: 15,
    backgroundColor: "#FFFFFF",
    color: "#6B6B6B",
    border: "1px solid #EAEAEA",
    boxShadow: "unset",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#EAEAEA !important",
    },
  },
  saveStudentButton: {
    width: "100%",
    marginTop: 10,
    marginLeft: 0,
    borderRadius: 0,
    padding: 15,
    backgroundColor: "#E7E7E7",
    color: "#4E4E4E",
    fontWeight: 600,
    boxShadow: "unset",
    "&:hover": {
      backgroundColor: "#D8D8D8 !important",
    },
  },
  closeTextWrapper: {
    float: "right",
    margin: "7px 4px 0 0",
    cursor: "pointer",
  },
  closeText: {
    color: "#474747",
    fontWeight: 600,
    textDecoration: "underline",
  },
  studentSearchInput: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

export const createAndMergeStudentIds = (students: IStudent[]) => {
  const temp = _.cloneDeep(students);
  const studentIds = temp.map((item: any) => {
    return item ? item.id : item;
  });

  return studentIds;
};

export const mergeStudentIds = (selectedMarkers: any) => {
  let temp: any = [];

  selectedMarkers.forEach((selectedMarker: any) => {
    const studentIds = selectedMarker.students.map((student: IStudent) => {
      return student.id;
    });
    temp = [...temp, ...studentIds];
  });

  return temp;
};

const AddStudentButton = ({ handleAddStudent, school }: IAddStudentButton) => {
  const [open, setOpen] = useState<boolean>(false);
  const [students, setStudents] = useState<IStudent[]>([]);
  const [studentAddresses, setStudentAddresses] = useState<IStudentAddress[]>(
    []
  );
  const [selectedStudent, setSelectedStudent] = useState<IStudent>(null);
  const [selectedStudentAddress, setSelectedStudentAddress] =
    useState<IStudentAddress>(null);
  const [isDisable, setIsDisable] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");

  const classes = useStyles();
  const provider = useDataProvider();

  const handleOpenDialog = () => {
    setOpen(true);
    setSelectedStudent(null);
    setSelectedStudentAddress(null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getStudents = useCallback(
    debounce((searchString: string) => {
      const filter = { SchoolId: school.id };

      provider
        .customGetListWithStaticParams(
          `students?filter=${JSON.stringify(
            filter
          )}&pageSize=50&search=${searchString}`
        )
        .then((result: any) => {
          setStudents(result.data as IStudent[]);
        });
    }, 1000),
    []
  );

  useEffect(() => {
    if (searchValue !== "") {
      getStudents(searchValue);
    }
  }, [searchValue, getStudents]);

  useEffect(() => {
    if (selectedStudent !== null && selectedStudentAddress !== null) {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
  }, [selectedStudent, selectedStudentAddress]);

  useEffect(() => {
    if (selectedStudent !== null) {
      provider
        .getList("studentAddresses", {
          filter: { StudentId: Number(selectedStudent.id) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then((result) => {
          setStudentAddresses(result.data as IStudentAddress[]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent]);

  return (
    <>
      <Button
        label="Add Student"
        alignIcon="right"
        className={classes.addStudentButton}
        onClick={handleOpenDialog}
      >
        <AddCircleOutlineIcon />
      </Button>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="xs"
      >
        <DialogTitle id="alert-dialog-title">
          Add The Student
          <Link
            aria-label="close"
            className={classes.closeTextWrapper}
            onClick={handleClose}
          >
            <Typography variant="body2" className={classes.closeText}>
              Cancel
            </Typography>
          </Link>
        </DialogTitle>
        <DialogContent>
          <SimpleForm toolbar={null}>
            <SanitizedGrid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Select Student</Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  className={classes.studentSearchInput}
                  freeSolo
                  disableClearable
                  options={students.map((option: IStudent) => {
                    return `${option.id} - ${option.firstName} ${option.lastName}`;
                  })}
                  onChange={(event, value) => {
                    const splitedValue = value.split(" ");

                    setSelectedStudent(
                      _.find(students, ["id", Number(splitedValue[0])])
                    );
                  }}
                  onInputChange={(event, newInputValue) => {
                    setSearchValue(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Student"
                      variant="outlined"
                      InputProps={{ ...params.InputProps, type: "search" }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  Select Student address
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  className={classes.studentSearchInput}
                  freeSolo
                  disableClearable
                  options={studentAddresses.map((option: IStudentAddress) => {
                    return `${option.id} - ${option.blockNumber} ${
                      option.street
                    } ${option.postalCode} - ${option.isMain ? "MAIN" : "SUB"}`;
                  })}
                  onChange={(event, value) => {
                    const splitedValue = value.split(" ");

                    setSelectedStudentAddress(
                      _.find(studentAddresses, ["id", Number(splitedValue[0])])
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Student Address"
                      variant="outlined"
                      InputProps={{ ...params.InputProps, type: "search" }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  label="Save"
                  className={classes.saveStudentButton}
                  onClick={() => {
                    handleAddStudent(selectedStudent, selectedStudentAddress);
                    handleClose();
                  }}
                  disabled={isDisable}
                />
              </Grid>
            </SanitizedGrid>
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddStudentButton;
